/**
 * Form Field Data Mapping Utility
 * Handles mapping form fields to employee/enrollment data based on configurations
 */

import { Employee, Enrollment, Plan } from "@shared/schema";
import { SYSTEM_DATA_SOURCES } from "@shared/schemas/forms";

/** Paths registered as type "date" in SYSTEM_DATA_SOURCES (employee/dependent DOB, etc.) */
const DATE_FIELD_PATHS = new Set<string>(
  Object.values(SYSTEM_DATA_SOURCES).flatMap((source) =>
    source.fields.filter((f) => f.type === "date").map((f) => f.path)
  )
);

/** Checkbox flags without derivedValue (actual boolean DB/computed fields) */
const BOOLEAN_FIELD_PATHS = new Set<string>(
  Object.values(SYSTEM_DATA_SOURCES).flatMap((source) =>
    source.fields
      .filter((f) => f.type === "checkbox" && !("derivedValue" in f && f.derivedValue))
      .map((f) => f.path)
  )
);

function isDateFieldPath(path: string): boolean {
  return DATE_FIELD_PATHS.has(path.trim());
}

function normalizeForMatch(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "");
}

/**
 * Derived checkbox fields (e.g. employee.sexMale) carry a `derivedValue` and
 * share a `dbColumn` with a sibling base field (e.g. employee.sex). At fill
 * time we resolve them to "X"/"" by comparing the base field's value on the
 * source record against the derivedValue. Pre-computed per source key.
 */
type DerivedFieldDef = { leaf: string; baseLeaf: string; derivedValue: string };

const DERIVED_FIELDS_BY_SOURCE: Record<string, DerivedFieldDef[]> = (() => {
  const map: Record<string, DerivedFieldDef[]> = {};
  for (const [sourceKey, source] of Object.entries(SYSTEM_DATA_SOURCES)) {
    const defs: DerivedFieldDef[] = [];
    const fields = source.fields as unknown as any[];
    for (const field of fields) {
      const derivedValue = field.derivedValue as string | undefined;
      if (!derivedValue || !field.dbColumn) continue;
      const base = fields.find(
        (f) => f.dbColumn === field.dbColumn && !f.derivedValue
      );
      if (!base) continue;
      defs.push({
        leaf: String(field.path).split(".").pop()!,
        baseLeaf: String(base.path).split(".").pop()!,
        derivedValue,
      });
    }
    if (defs.length) map[sourceKey] = defs;
  }
  return map;
})();

/**
 * Compute derived checkbox leaves ("X"/"") for a source record using the
 * schema's derivedValue rules. Returns {} when the source has no derived
 * fields or the record is missing.
 */
export function deriveSourceFields(
  sourceKey: string,
  record: any
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!record) return out;
  const defs = DERIVED_FIELDS_BY_SOURCE[sourceKey];
  if (!defs) return out;
  for (const def of defs) {
    out[def.leaf] =
      normalizeForMatch(record[def.baseLeaf]) ===
      normalizeForMatch(def.derivedValue)
        ? "X"
        : "";
  }
  return out;
}

function isBooleanFieldPath(path: string): boolean {
  return BOOLEAN_FIELD_PATHS.has(path.trim());
}

function coerceBooleanFieldValue(value: unknown): string {
  if (value === true || value === "true") return "true";
  if (value === false || value === "false") return "false";
  if (value === 1 || value === "1") return "true";
  if (value === 0 || value === "0") return "false";
  if (value == null || value === "") return "false";
  const s = String(value).toLowerCase();
  if (s === "yes") return "true";
  if (s === "no") return "false";
  return s === "true" ? "true" : "false";
}

function logicalCheckMatches(
  actual: string | null | undefined,
  expected: string,
  fieldPath: string
): boolean {
  if (isBooleanFieldPath(fieldPath)) {
    return (
      coerceBooleanFieldValue(actual) === coerceBooleanFieldValue(expected)
    );
  }
  return String(actual ?? "").toLowerCase() === expected.toLowerCase();
}

export interface MappingContext {
  employee?: Employee;
  enrollment?: Enrollment;
  plan?: Plan;
  dependents?: any[];
}

/**
 * Get value from context object using dot notation path
 * @example
 * getValueFromPath({employee: {firstName: "John"}}, "employee.firstName") => "John"
 */
export function getValueFromPath(context: MappingContext, path: string): any {
  const parts = path.split(".");
  let current: any = context;

  for (const part of parts) {
    if (current == null) return null;
    current = current[part];
  }

  return current;
}

/**
 * Apply transformation to a value
 */
export function transformValue(
  value: any,
  transformationType: string,
  transformationConfig?: any,
  context?: MappingContext
): any {
  if (value == null) return "";

  switch (transformationType) {
    case "uppercase":
      return String(value).toUpperCase();
    case "lowercase":
      return String(value).toLowerCase();
    case "date_format":
      return formatDate(value, transformationConfig?.dateFormat);
    case "phone_format":
      return formatPhoneNumber(String(value));
    case "concat":
      if (transformationConfig?.fields && Array.isArray(transformationConfig.fields)) {
        const source = context ?? value;
        return transformationConfig.fields
          .map((field: string) => getValueFromPath(source, field))
          .filter(Boolean)
          .join(transformationConfig.separator ?? " ");
      }
      return value;
    default:
      return value;
  }
}

export type CarrierFieldMappingInput = {
  sourceType?: string | null;
  systemDataSource?: string | null;
  sourcePath?: string | null;
  systemFieldPath?: string | null;
  transformRule?: string | null;
  defaultValue?: string | null;
  matchValue?: string | null;
  matchPlanId?: number | null;
};

/**
 * Resolve a carrier-form field mapping (superadmin Form Library schema).
 * Used by admin preview (flat path lookup) and enrollment (nested context).
 */
function formatValueForPath(path: string, value: string | null | undefined): string | undefined {
  if (value == null || String(value) === "") return undefined;
  const trimmedPath = path.trim();
  return isDateFieldPath(trimmedPath) ? formatDate(value) : String(value);
}

export function resolveCarrierFieldMapping(
  mapping: CarrierFieldMappingInput,
  getValue: (path: string) => string | null | undefined
): string {
  const sourceType = mapping.sourceType ?? mapping.systemDataSource ?? "";
  const fieldPath = mapping.sourcePath ?? mapping.systemFieldPath ?? "";

  const resolvePathValue = (path: string) => formatValueForPath(path, getValue(path.trim()));

  if (sourceType === "composite" && mapping.transformRule) {
    return mapping.transformRule.replace(/\{([^}]+)\}/g, (_, path: string) => {
      return resolvePathValue(path) ?? "";
    });
  }

  if (sourceType === "static") {
    return mapping.defaultValue ?? "";
  }

  // Plan-selection mapping: prefer matching by the bound system/library plan
  // id (robust against plan-code drift). The enrolled plan's identity is its
  // own id if it IS a library plan, else its systemPlanId link.
  if (mapping.matchPlanId != null) {
    const root = fieldPath.split(".")[0];
    const enrolledId =
      getValue(`${root}.systemPlanId`) || getValue(`${root}.id`);
    if (enrolledId != null && String(enrolledId) !== "") {
      return String(enrolledId) === String(mapping.matchPlanId) ? "X" : "";
    }
    // Fall through to planCode/matchValue matching when id is unavailable.
  }

  const val = fieldPath ? resolvePathValue(fieldPath) : undefined;
  if (mapping.matchValue) {
    return logicalCheckMatches(val, mapping.matchValue, fieldPath) ? "X" : "";
  }

  if (val !== undefined && val !== null && val !== "") {
    return val;
  }

  return mapping.defaultValue ?? "";
}

/**
 * Populate form fields with values from context
 */
export function populateFormFields(
  mappings: any[],
  context: MappingContext
): Record<string, any> {
  const populated: Record<string, any> = {};

  for (const mapping of mappings) {
    const sourceType = mapping.sourceType ?? mapping.systemDataSource;
    const hasCarrierShape =
      mapping.matchValue != null ||
      mapping.defaultValue != null ||
      mapping.transformRule != null ||
      sourceType === "composite" ||
      sourceType === "static";

    if (hasCarrierShape) {
      populated[mapping.formFieldId] = resolveCarrierFieldMapping(mapping, (path) => {
        const v = getValueFromPath(context, path);
        return v == null ? undefined : String(v);
      });
      continue;
    }

    const sourcePath = mapping.sourcePath ?? mapping.systemFieldPath ?? "";
    const value = getValueFromPath(context, sourcePath);
    const transformationType =
      (!mapping.transformationType || mapping.transformationType === "none") &&
      isDateFieldPath(sourcePath)
        ? "date_format"
        : mapping.transformationType || "none";
    const transformed = transformValue(
      value,
      transformationType,
      mapping.transformationConfig,
      context
    );

    if (mapping.conditionalLogic) {
      const ok = shouldDisplayField(mapping.conditionalLogic, context);
      const checkedValue = mapping.transformationConfig?.checkedValue ?? "X";
      const uncheckedValue = mapping.transformationConfig?.uncheckedValue ?? "";
      const hasExplicitCheckboxValues =
        mapping.transformationConfig?.checkedValue !== undefined ||
        mapping.transformationConfig?.uncheckedValue !== undefined;

      if (hasExplicitCheckboxValues) {
        populated[mapping.formFieldId] = ok ? checkedValue : uncheckedValue;
        continue;
      }

      populated[mapping.formFieldId] = ok ? transformed : "";
      continue;
    }

    populated[mapping.formFieldId] = transformed;
  }

  return populated;
}

/**
 * Format date value
 */
function formatDate(value: any, format: string = "MM/DD/YYYY"): string {
  if (!value) return "";

  const str = String(value).trim();
  const slashDate = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashDate) {
    const month = slashDate[1].padStart(2, "0");
    const day = slashDate[2].padStart(2, "0");
    const year = slashDate[3];
    return applyDateFormat(format, year, month, day);
  }

  const ymd = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    return applyDateFormat(format, ymd[1], ymd[2], ymd[3]);
  }

  const date = new Date(value);
  if (isNaN(date.getTime())) return str;

  // Use UTC methods to avoid local-timezone shifts on ISO date strings
  const year = String(date.getUTCFullYear());
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");

  return applyDateFormat(format, year, month, day);
}

function applyDateFormat(format: string, year: string, month: string, day: string): string {

  switch (format) {
    case "MM/DD/YYYY":
      return `${month}/${day}/${year}`;
    case "MM":
      return month;
    case "DD":
      return day;
    case "YYYY":
      return year;
    case "MMDDYYYY":
      return `${month}${day}${year}`;
    case "YYYYMMDD":
      return `${year}${month}${day}`;
    case "DDMMYYYY":
      return `${day}${month}${year}`;
    case "YYYY-MM-DD":
      return `${year}-${month}-${day}`;
    case "DD/MM/YYYY":
      return `${day}/${month}/${year}`;
    default:
      return `${month}/${day}/${year}`;
  }
}

/**
 * Format phone number
 */
function formatPhoneNumber(value: string): string {
  const cleaned = value.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return value;
}

/**
 * Check if a field should be displayed based on conditional logic
 */
export function shouldDisplayField(
  conditionalLogic: any,
  context: MappingContext
): boolean {
  if (!conditionalLogic) return true;

  const { field, operator, value } = conditionalLogic;
  const fieldValue = getValueFromPath(context, field);

  switch (operator) {
    case "equals":
      return fieldValue == value;
    case "not_equals":
      return fieldValue != value;
    case "contains":
      return String(fieldValue).includes(String(value));
    case "in":
      return Array.isArray(value) && value.includes(fieldValue);
    case "not_in":
      return !Array.isArray(value) || !value.includes(fieldValue);
    default:
      return true;
  }
}

/**
 * Validate form submission data against mapped fields
 */
export function validateFormSubmission(
  submissionData: Record<string, any>,
  formFields: any[]
): { valid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};

  for (const field of formFields) {
    const val = submissionData[field.id];
    if (field.required && (val == null || val === "")) {
      errors[field.id] = `${field.fieldLabel} is required`;
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}
